import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, CardMedia, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalculateIcon from '@mui/icons-material/Calculate';
import { getDeviceImage } from '../utils/deviceImages';

interface AirwayDevice {
  Size: string;
  'ID (mm)': string;
  'OD (mm)': string;
  'Neo Length (mm)'?: string;
  'Ped Length (mm)'?: string;
  'Long Length (mm)'?: string;
  'Flextend Length (mm)'?: string;
  'Transverse diameter (mm)'?: string;
  'AP Diameter (mm)'?: string;
}

interface RecommendedDevice {
  manufacturer: string;
  device: Partial<AirwayDevice>;
}

interface MobileDeviceSectionProps {
  recommendedDevices: RecommendedDevice[];
  weight: number | null;
  ettCalculations?: any;
  onNavigateToCalculations?: () => void;
}

export const MobileDeviceSection: React.FC<MobileDeviceSectionProps> = React.memo(({
  recommendedDevices,
  weight,
  ettCalculations,
  onNavigateToCalculations
}) => {
  const laryngoscopeDevices = recommendedDevices.filter(({ manufacturer }) => 
    ['Mac', 'Miller', 'Parsons', 'Lindholm'].includes(manufacturer)
  );

  const bronchoscopeDevices = recommendedDevices.filter(({ manufacturer }) => 
    manufacturer === 'Bronchoscope (Storz)'
  );

  const ettDevices = recommendedDevices.filter(({ manufacturer }) => 
    manufacturer === 'Portex Endotracheal Tube'
  );

  const lmaDevices = recommendedDevices.filter(({ manufacturer }) => 
    manufacturer === 'Laryngeal Mask'
  );

  const tracheostomyDevices = recommendedDevices.filter(({ manufacturer }) => 
    ['Shiley Cuffed', 'Bivona Flextend TTS Cuffed', 'Bivona Flextend PLUS TTS', 'Bivona Hyperflex TTS', 'Tracoe Mini'].includes(manufacturer)
  );

  const renderDeviceCard = (devices: RecommendedDevice[], title: string, icon: string) => {
    if (devices.length === 0) return null;

    // Get the first device's image (if available) for the card header
    const firstDevice = devices[0];
    const deviceImage = firstDevice ? getDeviceImage(firstDevice.manufacturer) : null;

    return (
      <Card 
        sx={{ 
          mb: 2, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2
        }}
      >
        {deviceImage && deviceImage.available && (
          <CardMedia
            component="img"
            height="120"
            image={deviceImage.src}
            alt={deviceImage.alt}
            sx={{ 
              objectFit: 'contain',
              backgroundColor: '#f8f9fa',
              p: 1
            }}
          />
        )}
        <CardContent sx={{ p: 2 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              color: '#1976d2',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textAlign: 'left',
              fontSize: '1rem'
            }}
          >
            {icon} {title}
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: 1 
          }}>
            {devices.map(({ manufacturer, device }, index) => (
              <Box
                key={index}
                sx={{
                  p: 1.5,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  textAlign: 'center',
                  minHeight: 80,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0 }}>
                  {manufacturer.replace(/^(Bivona |Storz |Portex )/, '')}
                </Typography>
                <Chip
                  label={`${device.Size || device['ID (mm)']}`}
                  size="medium"
                  color="primary"
                  sx={{ 
                    fontSize: '0.8rem',
                    height: '32px',
                    borderRadius: '50px',
                    width: '56px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0.5,
                      fontWeight: 600,
                      textAlign: 'center'
                    }
                  }}
                />
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderTraumeostomySection = () => {
    if (tracheostomyDevices.length === 0) return null;

    return (
      <Card 
        sx={{ 
          mb: 2, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              color: '#1976d2',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textAlign: 'left',
              fontSize: '1rem'
            }}
          >
            Tracheostomy Tubes
          </Typography>
          
          {tracheostomyDevices.map(({ manufacturer, device }, index) => (
            <Accordion key={index} sx={{ mb: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 48 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {manufacturer}
                  </Typography>
                  <Chip
                    label={`${device.Size}`}
                    size="medium"
                    color="primary"
                    sx={{ 
                      fontSize: '0.8rem',
                      height: '32px',
                      borderRadius: '50px',
                      width: '56px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      '& .MuiChip-label': {
                        px: 1,
                        py: 0.5,
                        fontWeight: 600,
                        textAlign: 'center'
                      }
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                  gap: 1 
                }}>
                  {weight !== null && weight <= 5 && device['Neo Length (mm)'] && (
                    <Box sx={{ p: 1, backgroundColor: '#f0f7ff', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="caption" display="block">Neo Length</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {device['Neo Length (mm)']} mm
                      </Typography>
                    </Box>
                  )}
                  {device['Ped Length (mm)'] && (
                    <Box sx={{ p: 1, backgroundColor: '#f0f7ff', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="caption" display="block">Ped Length</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {device['Ped Length (mm)']} mm
                      </Typography>
                    </Box>
                  )}
                  {device['Long Length (mm)'] && (
                    <Box sx={{ p: 1, backgroundColor: '#fff4e6', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="caption" display="block">Long Length</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {device['Long Length (mm)']} mm
                      </Typography>
                    </Box>
                  )}
                  {device['Flextend Length (mm)'] && (
                    <Box sx={{ p: 1, backgroundColor: '#f0fff0', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="caption" display="block">Flextend Length</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {device['Flextend Length (mm)']} mm
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>
    );
  };

  const renderCombinedAirwayCard = () => {
    const allAirwayDevices = [
      ...bronchoscopeDevices,
      ...ettDevices, 
      ...lmaDevices
    ];

    if (allAirwayDevices.length === 0) return null;

    // Get cuffed ETT calculations for the range
    const getCuffedETTRange = () => {
      if (!ettCalculations) return null;

      const cuffedCalculations = [];
      if (ettCalculations.idealCalculations.motoyama !== null) {
        cuffedCalculations.push({ name: 'Motoyama', value: ettCalculations.idealCalculations.motoyama });
      }
      if (ettCalculations.idealCalculations.khine !== null) {
        cuffedCalculations.push({ name: 'Khine', value: ettCalculations.idealCalculations.khine });
      }
      if (ettCalculations.idealCalculations.duracher !== null) {
        cuffedCalculations.push({ name: 'Duracher', value: ettCalculations.idealCalculations.duracher });
      }
      if (ettCalculations.idealCalculations.weightBased !== null) {
        cuffedCalculations.push({ name: 'Weight-based', value: ettCalculations.idealCalculations.weightBased });
      }

      if (cuffedCalculations.length === 0) return null;

      const minValue = Math.min(...cuffedCalculations.map(c => c.value));
      const maxValue = Math.max(...cuffedCalculations.map(c => c.value));
      
      const roundToNearestHalf = (value: number) => {
        return Math.round(value * 2) / 2;
      };

      const formatValue = (value: number) => {
        const rounded = roundToNearestHalf(value);
        return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
      };

      return {
        range: `${formatValue(minValue)} - ${formatValue(maxValue)}`,
        formulas: cuffedCalculations.map(c => c.name).join(', ')
      };
    };

    const cuffedETTRange = getCuffedETTRange();

    return (
      <Card 
        sx={{ 
          mb: 2, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              color: '#1976d2',
              textAlign: 'left',
              fontSize: '1rem'
            }}
          >
            Airway Devices
          </Typography>

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
            gap: 1.5 
          }}>
            {allAirwayDevices.map(({ manufacturer, device }, index) => (
              <Box
                key={index}
                sx={{
                  p: 1,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  textAlign: 'center',
                  minHeight: 80,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 0 }}>
                  {manufacturer.replace(/^(Bivona |Storz |Portex )/, '').replace(' (Storz)', '').replace('Endotracheal Tube', 'Cuffed ETT')}
                </Typography>
                <Chip
                  label={`${device.Size || device['ID (mm)']}`}
                  size="medium"
                  color="primary"
                  sx={{ 
                    fontSize: '0.8rem',
                    height: '32px',
                    borderRadius: '50px',
                    width: '56px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '& .MuiChip-label': {
                      px: 1,
                      py: 0.5,
                      fontWeight: 600,
                      textAlign: 'center'
                    }
                  }}
                />
              </Box>
            ))}
          </Box>
          
          {/* Cuffed ETT Range Section */}
          {cuffedETTRange && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              backgroundColor: '#f0f7ff', 
              borderRadius: 1,
              border: '1px solid #e3f2fd'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  color: '#1976d2',
                  mb: 1,
                  textAlign: 'center'
                }}
              >
                Cuffed ETT Range
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mb: 1
              }}>
                <Chip 
                  label={cuffedETTRange.range}
                  color="primary"
                  variant="filled"
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1rem',
                    height: '36px'
                  }}
                />
              </Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#666', 
                  textAlign: 'center',
                  display: 'block',
                  fontStyle: 'italic'
                }}
              >
                Based on {cuffedETTRange.formulas} formulas
              </Typography>
            </Box>
          )}

          {/* View All Calculations Button */}
          {onNavigateToCalculations && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={onNavigateToCalculations}
                startIcon={<CalculateIcon />}
                size="small"
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    borderColor: '#1976d2'
                  }
                }}
              >
                View All Calculations
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };


  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      {renderDeviceCard(laryngoscopeDevices, 'Laryngoscopes', '')}
      {renderCombinedAirwayCard()}
      {renderTraumeostomySection()}
    </Box>
  );
});